import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UnreadIcon,
  OpenInNew as OpenInNewIcon,
  SmartToy as SmartToyIcon,
  Lock as LockIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { fetchWithAuth } from '../../../utils/auth';
import API_BASE_URL from '../../../config/api';
import { getButtonStyles, BUTTON_COLORS } from '../../../config/buttonStyles';
import { useTheme } from '../../../utils/ThemeContext';

const ChatInterface = React.forwardRef(({ 
  selectedArticles = [], 
  suggestions = [], 
  totalArticles = 0, 
  onMessageSent, 
  onNewChat, 
  selectedHistoryItem, 
  currentConversationId: parentConversationId 
}, ref) => {
  const { isDarkMode } = useTheme();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationArticles, setConversationArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [isSharedConversation, setIsSharedConversation] = useState(false);
  const messagesEndRef = useRef(null);

  useImperativeHandle(ref, () => ({
    startNewChat: () => {
      setMessages([]);
      setInputMessage('');
      setError(null);
      setCurrentConversationId(null);
      setConversationArticles([]);
      setReadOnlyMode(false);
      setIsSharedConversation(false);
    },
    loadConversation: async (conversationId) => {
      try {
        setIsSharedConversation(false); // Reset flag for regular conversations
        const response = await fetchWithAuth(`/api/ai/conversation/${conversationId}`);
        const data = await response.json();
        if (data.messages) {
          setMessages(data.messages);
          setCurrentConversationId(conversationId);
          // Articles will be loaded automatically by useEffect
        }
      } catch (err) {
        console.error('Error loading conversation:', err);
        setMessages([]);
      }
    },
    loadSharedConversation: (sharedConversation, isReadOnly = false) => {
      if (sharedConversation) {
        if (sharedConversation.conversation_messages && sharedConversation.conversation_messages.length > 0) {
          // Full conversation with messages
          setIsSharedConversation(true); // Set flag before setting conversation ID
          setMessages(sharedConversation.conversation_messages);
          setCurrentConversationId(sharedConversation.id);
          setConversationArticles([]); // Shared conversations don't have associated articles
          setError(null);
        } else {
          // Basic shared conversation (no actual messages exist)
          setIsSharedConversation(true); // Set flag before setting conversation ID
          const basicMessages = [
            {
              role: 'assistant',
              content: `This AI conversation was shared with you by ${sharedConversation.shared_by || 'a colleague'}.`,
              timestamp: sharedConversation.timestamp || new Date().toISOString(),
              conversation_id: sharedConversation.id
            },
            {
              role: 'assistant', 
              content: 'The original conversation content is not available. This may be because the conversation was created during testing or the messages were not properly saved.',
              timestamp: sharedConversation.timestamp || new Date().toISOString(),
              conversation_id: sharedConversation.id
            },
            {
              role: 'assistant',
              content: isReadOnly ? 'This is a read-only shared conversation. You cannot send messages.' : 'You can start a new conversation about this topic by asking questions below.',
              timestamp: sharedConversation.timestamp || new Date().toISOString(),
              conversation_id: sharedConversation.id
            }
          ];
          setMessages(basicMessages);
          setCurrentConversationId(sharedConversation.id);
          setConversationArticles([]);
          setError(null);
        }
        
        // Set read-only mode
        if (isReadOnly) {
          setReadOnlyMode(true);
        }
      }
    }
  }));

  useEffect(() => {
    // Prioritize selectedHistoryItem over parentConversationId
    if (selectedHistoryItem) {
      const conversationId = selectedHistoryItem.conversation_id || selectedHistoryItem.id;
      setCurrentConversationId(conversationId);
    } else if (parentConversationId) {
      setCurrentConversationId(parentConversationId);
    } else {
      setCurrentConversationId(null);
    }
  }, [selectedHistoryItem, parentConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation and articles when conversation ID changes
  useEffect(() => {
    if (currentConversationId) {
      // Skip API call for shared conversations since messages are already loaded
      if (!isSharedConversation) {
        // Load conversation messages
        const loadConversationData = async () => {
          try {
            const response = await fetchWithAuth(`/api/ai/conversation/${currentConversationId}`);
            const data = await response.json();
            if (data.messages) {
              setMessages(data.messages);
            }
          } catch (err) {
            console.error('Error loading conversation:', err);
            setMessages([]);
          }
        };
        loadConversationData();
        // Load articles
        loadConversationArticles(currentConversationId);
      } else {
        // For shared conversations, we don't load articles since they're not associated
        setConversationArticles([]);
      }
    } else {
      setMessages([]);
      setConversationArticles([]);
      setIsSharedConversation(false); // Reset shared conversation flag
    }
  }, [currentConversationId, isSharedConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversationArticles = async (conversationId) => {
    if (!conversationId) {
      setConversationArticles([]);
      return;
    }
    
    try {
      setArticlesLoading(true);
      const response = await fetchWithAuth(`/api/ai/conversation/${conversationId}/articles`);
      const data = await response.json();
      if (data.articles) {
        setConversationArticles(data.articles);
      } else {
        setConversationArticles([]);
      }
    } catch (err) {
      console.error('❌ Error loading conversation articles:', err);
      setConversationArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || readOnlyMode) return;

    const userMessage = inputMessage.trim();
    
    // Check if this is a new conversation (no currentConversationId or no conversation articles)
    const isNewConversation = !currentConversationId || conversationArticles.length === 0;
    
    // For new conversations, validate that articles are selected
    if (isNewConversation && (!selectedArticles || selectedArticles.length === 0)) {
      // Show professional message without calling API
      const newUserMessage = {
        role: 'user',
        content: userMessage,
        timestamp: new Date().toISOString()
      };
      
      const promptMessage = {
        role: 'assistant',
        content: 'Hello! I would be happy to assist you. To provide you with accurate and contextual insights, please select up to 4 articles from your research collection and feel free to ask any questions you have about them.',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, newUserMessage, promptMessage]);
      setInputMessage('');
      return; // Don't proceed with API call
    }
    
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    const newMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);

    try {
      // For existing conversations, use conversation articles as knowledge base
      // For new conversations, use selected articles from parent
      let articleIdsToUse = [];
      
      if (currentConversationId && conversationArticles.length > 0) {
        // Use articles from the current conversation (locked knowledge base)
        articleIdsToUse = conversationArticles.map(article => article.id);
      } else {
        // Use selected articles from parent (for new conversations or when conversation has no articles yet)
        articleIdsToUse = (selectedArticles || []).map(article => article.id);
      }
      
      const response = await fetchWithAuth('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          selected_article_ids: articleIdsToUse,
          conversation_id: currentConversationId
        })
      });

      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        const aiMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, aiMessage]);
        
        // Update conversation ID if this is a new conversation
        if (data.conversation_id && !currentConversationId) {
          setCurrentConversationId(data.conversation_id);
        }
        
        // Notify parent component
        if (onMessageSent && typeof onMessageSent === 'function') {
          onMessageSent();
        }
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Error sending message:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
  };

  const handleArticleClick = (article) => {
    // Use requestAnimationFrame for smoother opening
    requestAnimationFrame(() => {
      setSelectedArticle(article);
      setArticleModalOpen(true);
    });
  };

  const handleCloseArticleModal = () => {
    setArticleModalOpen(false);
    setSelectedArticle(null);
  };

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography variant="h6" className="text-neon" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon style={{ fontSize: '1.2rem', color: '#468230' }} />
          AI Research Assistant
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {/* Knowledge Base Indicator */}
          {currentConversationId && conversationArticles.length > 0 ? (
            <Chip 
              icon={<LockIcon style={{ fontSize: '1rem', color: '#6c3082' }} />}
              label={`${conversationArticles.length} conversation articles`}
              className="btn-modern-secondary"
              size="small"
              sx={{
                backgroundColor: 'rgba(70, 130, 48, 0.2)',
                border: '1px solid rgba(70, 130, 48, 0.5)',
                color: '#468230'
              }}
            />
          ) : (
            <Chip 
              icon={<PublicIcon style={{ fontSize: '1rem', color: '#468230' }} />}
              label={`${(selectedArticles || []).length} selected articles`}
              className="btn-modern-secondary"
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid var(--glass-border)',
                color: 'var(--text-primary)'
              }}
            />
          )}
          <Chip 
            label={`${totalArticles || 0} total articles`}
            className="btn-modern-secondary"
            size="small"
          />
        </Box>
      </Box>

      {/* Articles - Show conversation articles OR selected articles for new conversations */}
      {(() => {
        const hasConversationArticles = currentConversationId && conversationArticles.length > 0;
        const hasSelectedArticles = !currentConversationId && selectedArticles && selectedArticles.length > 0;
        const shouldShow = hasConversationArticles || hasSelectedArticles || articlesLoading;
        
        return shouldShow;
      })() && (
        <Accordion 
          defaultExpanded 
          sx={{ 
            mb: 2,
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon style={{ color: '#468230' }} />}
            sx={{
              '& .MuiAccordionSummary-content': {
                margin: '0.5rem 0'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ArticleIcon style={{ color: '#468230' }} />
              <Typography variant="h6" style={{ color: 'var(--text-primary)' }}>
                {currentConversationId && conversationArticles.length > 0
                  ? `Articles in this conversation (${conversationArticles.length})`
                  : `Selected articles (${selectedArticles?.length || 0})`
                }
              </Typography>
              {currentConversationId && conversationArticles.length > 0 ? (
                <Chip 
                  label="Locked Knowledge Base"
                  icon={<LockIcon style={{ fontSize: '0.75rem', color: '#6c3082' }} />}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(70, 130, 48, 0.2)',
                    border: '1px solid rgba(70, 130, 48, 0.5)',
                    color: '#468230',
                    fontSize: '0.75rem',
                    height: '20px'
                  }}
                />
              ) : (
                <Chip 
                  label="New Conversation"
                  icon={<PublicIcon style={{ fontSize: '0.75rem', color: '#468230' }} />}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(108, 48, 130, 0.2)',
                    border: '1px solid rgba(108, 48, 130, 0.5)',
                    color: '#6c3082',
                    fontSize: '0.75rem',
                    height: '20px'
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
                      <AccordionDetails>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                maxHeight: '300px',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#468230',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(70, 130, 48, 0.8)',
                  },
                },
              }}>
                {articlesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress style={{ color: '#468230' }} size={24} />
                  </Box>
                ) : (
                  // Show conversation articles if they exist, otherwise show selected articles
                  (currentConversationId && conversationArticles.length > 0 
                    ? conversationArticles 
                    : (selectedArticles || [])
                  ).map((article, index) => (
                                  <Card 
                    key={article.id || index}
                    sx={{
                      // Read articles: Green theme
                      ...(article.read ? {
                        background: 'rgba(70, 130, 48, 0.15)',
                        border: '1px solid rgba(70, 130, 48, 0.3)',
                        boxShadow: '0 2px 8px rgba(70, 130, 48, 0.2)',
                        '&:hover': {
                          background: 'rgba(70, 130, 48, 0.2)',
                          borderColor: 'rgba(70, 130, 48, 0.5)',
                          boxShadow: '0 4px 12px rgba(70, 130, 48, 0.3)'
                        }
                      } : {
                        // Unread articles: Orange theme
                        background: 'rgba(255, 152, 0, 0.15)',
                        border: '1px solid rgba(255, 152, 0, 0.3)',
                        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.2)',
                        '&:hover': {
                          background: 'rgba(255, 152, 0, 0.2)',
                          borderColor: 'rgba(255, 152, 0, 0.5)',
                          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                        }
                      }),
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      minHeight: '120px',
                      '&:hover': {
                        transform: 'translateY(-1px)'
                      }
                    }}
                    onClick={() => handleArticleClick(article)}
                  >
                    <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexShrink: 0 }}>
                          {article.read ? (
                            <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                          ) : (
                            <UnreadIcon style={{ color: '#ff9800', fontSize: '1.2rem' }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              color: 'var(--text-primary)',
                              fontWeight: article.read ? 'normal' : 'bold',
                              marginBottom: '0.5rem',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {article.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'var(--text-muted)',
                              marginBottom: '0.5rem',
                              fontSize: '0.875rem'
                            }}
                          >
                            {article.authors} • {article.journal} • {article.year}
                          </Typography>
                          {article.abstract && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'var(--text-muted)',
                                opacity: 0.8,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                                fontSize: '0.8rem'
                              }}
                            >
                              {article.abstract}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
              ))
                )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Messages Area */}
      <div style={{ 
        flex: 1, 
        overflow: 'auto',
        marginBottom: '1rem',
        minHeight: 0
      }}>
        {(messages || []).length === 0 ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            padding: '1rem'
          }}>
            <Avatar style={{ 
              background: 'linear-gradient(135deg, #468230, #5cb85c)',
              width: '64px',
              height: '64px',
              marginBottom: '1rem'
            }}>
              <PsychologyIcon fontSize="large" style={{ color: '#ffffff' }} />
            </Avatar>
            <Typography variant="h6" className="text-neon" gutterBottom>
              Welcome to AI Research Assistant
            </Typography>
            <Typography variant="body1" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Ask questions about your research articles, request summaries, or get insights from the AI.
            </Typography>
            {/* Knowledge Base Info */}
            {currentConversationId && conversationArticles.length > 0 ? (
              <Box sx={{ 
                backgroundColor: 'rgba(70, 130, 48, 0.1)', 
                border: '1px solid rgba(70, 130, 48, 0.3)', 
                borderRadius: 'var(--radius-md)', 
                p: 2, 
                mb: 2,
                maxWidth: '400px'
              }}>
                <Typography variant="body2" style={{ color: '#468230', fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <LockIcon style={{ fontSize: '1rem', color: '#6c3082' }} />
                  Conversation-Specific Knowledge Base
                </Typography>
                <Typography variant="body2" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  AI responses are limited to the {conversationArticles.length} articles used in this conversation for focused, relevant answers.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid var(--glass-border)', 
                borderRadius: 'var(--radius-md)', 
                p: 2, 
                mb: 2,
                maxWidth: '400px'
              }}>
                <Typography variant="body2" style={{ color: 'var(--text-primary)', fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <PublicIcon style={{ fontSize: '1rem', color: '#468230' }} />
                  Global Knowledge Base
                </Typography>
                <Typography variant="body2" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  AI can access all your articles. Select specific articles above to limit the knowledge base.
                </Typography>
              </Box>
            )}
            {(suggestions || []).length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2" className="text-neon" gutterBottom>
                  Suggested questions:
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                  {(suggestions || []).map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="btn-modern-secondary"
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
              </Box>
            )}
          </div>
        ) : (
          <List>
            {(messages || []).map((message, index) => (
              <ListItem
                key={index}
                style={{
                  padding: '1.5rem 0',
                  flexDirection: 'column',
                  alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  maxWidth: '80%',
                  gap: '0.75rem'
                }}>
                  {message.role === 'assistant' && (
                    <Avatar style={{ 
                      background: 'linear-gradient(135deg, #468230, #5cb85c)',
                      width: '32px',
                      height: '32px'
                    }}>
                      <PsychologyIcon fontSize="small" style={{ color: '#ffffff' }} />
                    </Avatar>
                  )}
                  
                  <div className="modern-card" style={{
                    background: message.role === 'user' 
                      ? 'rgba(108, 48, 130, 0.15)' 
                      : 'rgba(70, 130, 48, 0.15)',
                    border: message.role === 'user'
                      ? '1px solid rgba(108, 48, 130, 0.3)'
                      : '1px solid rgba(70, 130, 48, 0.3)',
                    padding: '1rem',
                    position: 'relative'
                  }}>
                    <Typography variant="body1" style={{ 
                      color: 'var(--text-primary)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6
                    }}>
                      {message.content}
                    </Typography>
                    <Typography variant="caption" style={{ 
                      color: 'var(--text-muted)',
                      display: 'block',
                      marginTop: '0.5rem',
                      opacity: 0.7
                    }}>
                      {message.role === 'user' ? 'You' : 'AI Assistant'} • {new Date(message.timestamp).toLocaleTimeString()}
                    </Typography>
                  </div>

                  {message.role === 'user' && (
                    <Avatar style={{ 
                      background: 'linear-gradient(135deg, #6c3082, #8e44ad)',
                      width: '32px',
                      height: '32px'
                    }}>
                      <PersonIcon fontSize="small" style={{ color: '#ffffff' }} />
                    </Avatar>
                  )}
                </div>
              </ListItem>
            ))}
            <div ref={messagesEndRef} />
          </List>
        )}

        {isLoading && (
          <div className="modern-card" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '1rem',
            background: 'rgba(70, 130, 48, 0.15)',
            border: '1px solid rgba(70, 130, 48, 0.3)',
            marginTop: '1rem'
          }}>
            <Avatar style={{ 
              background: 'linear-gradient(135deg, #468230, #5cb85c)',
              width: '32px',
              height: '32px'
            }}>
              <PsychologyIcon fontSize="small" style={{ color: '#ffffff' }} />
            </Avatar>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className="loading-spinner-modern" style={{ width: '20px', height: '20px' }}></div>
              <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
                AI is thinking...
              </Typography>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          className="modern-card"
          style={{ 
            marginBottom: '1rem',
            background: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            color: '#ff6b6b'
          }}
        >
          {error}
        </Alert>
      )}

      {/* Input Area */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem',
        alignItems: 'flex-end',
        flexShrink: 0
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={readOnlyMode ? "This is a read-only shared conversation" : "Ask your AI research assistant..."}
          variant="outlined"
          disabled={isLoading || readOnlyMode}
          className="input-modern"
          data-tutorial="chat-input"
          sx={{
            '& .MuiOutlinedInput-root': {
              background: 'var(--surface-2)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--text-primary)',
              '& fieldset': {
                borderColor: 'var(--glass-border)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(108, 48, 130, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6c3082',
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              color: 'var(--text-primary)',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'var(--text-muted)',
              opacity: 1,
            },
          }}
        />
        <Button
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading || readOnlyMode}
          className="btn-modern"
          sx={{
            ...getButtonStyles(false, isDarkMode, 'generic'),
            minWidth: 'auto',
            padding: '0.75rem',
            height: '56px'
          }}
        >
          <SendIcon />
        </Button>
      </div>

      {/* Article Details Modal */}
      <Dialog
        open={articleModalOpen}
        onClose={handleCloseArticleModal}
        maxWidth="xl"
        fullWidth
        transitionDuration={200}
        PaperProps={{
          sx: {
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(10px)',
            transform: 'translateZ(0)',
            willChange: 'transform',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
          }
        }}
      >
        {selectedArticle && (
          <>
            <DialogTitle sx={{ 
              color: 'var(--text-primary)',
              borderBottom: '1px solid var(--glass-border)',
              pb: 2,
              transform: 'translateZ(0)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {selectedArticle.read ? (
                  <CheckCircleIcon style={{ color: '#4caf50', fontSize: '1.2rem' }} />
                ) : (
                  <UnreadIcon style={{ color: '#ff9800', fontSize: '1.2rem' }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: selectedArticle.read ? 'normal' : 'bold' }}>
                  Article Details
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ color: 'var(--text-primary)', lineHeight: 1.3 }}>
                {selectedArticle.title}
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2, transform: 'translateZ(0)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Authors */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#468230', mb: 0.5 }}>
                    Authors
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                    {selectedArticle.authors}
                  </Typography>
                </Box>

                {/* Journal and Year */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#468230', mb: 0.5 }}>
                      Journal
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                      {selectedArticle.journal}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#468230', mb: 0.5 }}>
                      Year
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
                      {selectedArticle.year}
                    </Typography>
                  </Box>
                </Box>

                {/* Abstract */}
                {selectedArticle.abstract && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#468230', mb: 0.5 }}>
                      Abstract
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'var(--text-primary)',
                        lineHeight: 1.6,
                        textAlign: 'justify'
                      }}
                    >
                      {selectedArticle.abstract}
                    </Typography>
                  </Box>
                )}

                {/* Read Status */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#468230', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip
                    label={selectedArticle.read ? "Read" : "Unread"}
                    size="small"
                    sx={{
                      backgroundColor: selectedArticle.read ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                      color: selectedArticle.read ? '#4caf50' : '#ff9800',
                      border: `1px solid ${selectedArticle.read ? '#4caf50' : '#ff9800'}`,
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ 
              borderTop: '1px solid var(--glass-border)',
              pt: 2,
              px: 3,
              pb: 3,
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ flex: 1 }}>
                {selectedArticle?.link && (
                  <Button 
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Safari-compatible approach
                      const link = document.createElement('a');
                      link.href = selectedArticle.link;
                      link.target = '_blank';
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="btn-modern btn-modern-primary"
                    startIcon={<OpenInNewIcon />}
                    sx={{
                      bgcolor: 'var(--accent-gradient)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'var(--accent-gradient)',
                        opacity: 0.9
                      }
                    }}
                  >
                    Open Article
                  </Button>
                )}
              </Box>
              
              <Button 
                onClick={handleCloseArticleModal}
                className="btn-modern-secondary"
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
});

export default ChatInterface; 